// src/components/live/UserResults.js
import React, { useState, useEffect } from 'react';
import { FaStar, FaComment, FaLightbulb, FaEye, FaEyeSlash } from 'react-icons/fa';
import { liveAPI } from '../../services/api';

const UserResults = ({ sessionId, userId }) => {
  const [userResults, setUserResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  const fetchUserResults = async () => {
    try {
      const data = await liveAPI.getUserResults(sessionId, userId);
      setUserResults(data.results);
    } catch (error) {
      setUserResults(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId && userId) {
      fetchUserResults();
      
      // Refresh every 15 seconds
      const interval = setInterval(fetchUserResults, 15000);
      return () => clearInterval(interval);
    }
  }, [sessionId, userId]);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-4 animate-pulse">
        <div className="h-6 bg-gray-200 rounded mb-2"></div>
        <div className="h-4 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!userResults) {
    return null; // Don't show anything if no results for this user
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <FaStar className="w-5 h-5 text-blue-500 mr-2" />
          Your Performance
        </h3>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="flex items-center space-x-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100"
        >
          {showDetails ? <FaEyeSlash className="w-3 h-3" /> : <FaEye className="w-3 h-3" />}
          <span>{showDetails ? 'Hide' : 'Show'} Details</span>
        </button>
      </div>

      {/* Score Overview */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{userResults.scores?.technique || 0}</div>
          <div className="text-xs text-gray-600">Technique</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">{userResults.scores?.composition || 0}</div>
          <div className="text-xs text-gray-600">Composition</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-pink-600">{userResults.scores?.creativity || 0}</div>
          <div className="text-xs text-gray-600">Creativity</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{userResults.scores?.overall || 0}</div>
          <div className="text-xs text-gray-600">Overall</div>
        </div>
      </div>

      {/* Total Score */}
      <div className="text-center mb-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl">
        <div className="text-3xl font-bold text-green-600 mb-1">
          {userResults.totalScore || 0}
        </div>
        <div className="text-sm text-gray-600">Total Score</div>
        <div className="text-xs text-gray-500 mt-1">
          Round {userResults.round || 1}
        </div>
      </div>

      {/* Adjudicator Comments - Only show if there are comments */}
      {showDetails && (userResults.remarks || userResults.improvements) && (
        <div className="space-y-4">
          {userResults.remarks && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <FaComment className="w-4 h-4 text-green-600" />
                <h4 className="font-semibold text-green-800">Adjudicator Remarks</h4>
              </div>
              <p className="text-green-700 text-sm">{userResults.remarks}</p>
            </div>
          )}

          {userResults.improvements && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-2">
                <FaLightbulb className="w-4 h-4 text-blue-600" />
                <h4 className="font-semibold text-blue-800">Areas for Improvement</h4>
              </div>
              <p className="text-blue-700 text-sm">{userResults.improvements}</p>
            </div>
          )}
        </div>
      )}

      {/* Ranking Info */}
      {userResults.rank && (
        <div className="mt-4 p-3 bg-gray-50 rounded-xl text-center">
          <div className="text-sm text-gray-600">
            Current Ranking: <span className="font-semibold text-gray-900">#{userResults.rank}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserResults;